import { useTranslation } from "react-i18next";
import {
    Boxes,
    Circle,
    FilePlus2,
    Maximize2,
    Minimize2,
    Palette,
    Plus,
    Scan,
    Shrink,
    StickyNote,
    Users as UsersIcon,
} from "lucide-react";

type Props = {
    onAddFigure: () => void;
    onAddNote: () => void;
    // Add a floor area. Omitted for the patient (areas are therapist-only).
    onAddArea?: () => void;
    onFit: () => void;
    // Therapist scene actions (optional): save the board as a scene, or start a
    // new session seeded from it.
    onSaveScene?: () => void;
    onNewSession?: () => void;
    saveSceneDisabled?: boolean;
    // Leave immersive entirely (back to the windowed layout). Omitted for the
    // patient, who stays in fullscreen.
    onExit?: () => void;
    // Toggle native OS fullscreen while staying immersive.
    onToggleNativeFullscreen: () => void;
    nativeFullscreen: boolean;
    // Owner-labels toggle. Omitted for the patient, where owners are always on.
    showOwners?: boolean;
    onToggleOwners?: () => void;
    // Patient: toggle the "my figures" list.
    onToggleList?: () => void;
    listOpen?: boolean;
    // Therapist-only: open the board-style modal (shape / color / panorama).
    onBoardStyle?: () => void;
    disabled?: boolean;
};

// White pill buttons on the blue bar, matching the therapist controls.
const btn =
    "flex h-11 w-11 items-center justify-center rounded-xl bg-white/90 text-brand-700 shadow-sm transition-colors hover:bg-white disabled:opacity-50";
const btnActive = "bg-white text-brand-700 ring-2 ring-white";

/** Floating action bar shown while the board is in immersive (fullscreen)
 *  mode: add figure/note/area, frame the board, native fullscreen — plus board
 *  styling for the therapist (issue #197). Blue bar to match the board UI. */
export default function ImmersiveToolbar({
    onAddFigure,
    onAddNote,
    onAddArea,
    onFit,
    onSaveScene,
    onNewSession,
    saveSceneDisabled,
    onExit,
    onToggleNativeFullscreen,
    nativeFullscreen,
    showOwners,
    onToggleOwners,
    onToggleList,
    listOpen,
    onBoardStyle,
    disabled,
}: Props) {
    const { t } = useTranslation();

    return (
        <div className="pointer-events-auto absolute bottom-5 left-1/2 z-[65] flex -translate-x-1/2 flex-wrap items-center justify-center gap-2 rounded-2xl bg-brand-600 p-2 shadow-xl">
            <button
                type="button"
                onClick={onAddFigure}
                disabled={disabled}
                title={t("constellation.patient.addFigureCta", "Add figure")}
                aria-label={t("constellation.patient.addFigureCta", "Add figure")}
                className={btn}
            >
                <Plus size={18} />
            </button>
            <button
                type="button"
                onClick={onAddNote}
                disabled={disabled}
                title={t("constellation.note.add")}
                aria-label={t("constellation.note.add")}
                className={btn}
            >
                <StickyNote size={18} />
            </button>
            {onAddArea && (
                <button
                    type="button"
                    onClick={onAddArea}
                    disabled={disabled}
                    title={t("constellation.zone.add")}
                    aria-label={t("constellation.zone.add")}
                    className={btn}
                >
                    <Circle size={18} />
                </button>
            )}

            {onToggleList && (
                <button
                    type="button"
                    onClick={onToggleList}
                    aria-pressed={listOpen}
                    title={t("constellation.list.title")}
                    aria-label={t("constellation.list.title")}
                    className={`${btn} ${listOpen ? btnActive : ""}`}
                >
                    <UsersIcon size={18} />
                </button>
            )}

            <span className="mx-1 h-6 w-px bg-white/40" />

            {onToggleOwners && (
                <button
                    type="button"
                    onClick={onToggleOwners}
                    aria-pressed={showOwners}
                    title={t("constellation.workspace.toggleOwners")}
                    aria-label={t("constellation.workspace.toggleOwners")}
                    className={`${btn} ${showOwners ? btnActive : ""}`}
                >
                    <UsersIcon size={18} />
                </button>
            )}
            <button
                type="button"
                onClick={onFit}
                title={t("constellation.workspace.fitToBoard")}
                aria-label={t("constellation.workspace.fitToBoard")}
                className={btn}
            >
                <Scan size={18} />
            </button>

            {(onSaveScene || onNewSession) && (
                <span className="mx-1 h-6 w-px bg-white/40" />
            )}
            {onSaveScene && (
                <button
                    type="button"
                    onClick={onSaveScene}
                    disabled={saveSceneDisabled}
                    title={t("constellation.scenes.save")}
                    aria-label={t("constellation.scenes.save")}
                    className={btn}
                >
                    <Boxes size={18} />
                </button>
            )}
            {onNewSession && (
                <button
                    type="button"
                    onClick={onNewSession}
                    disabled={saveSceneDisabled}
                    title={t("constellation.scenes.newSessionFromBoard")}
                    aria-label={t("constellation.scenes.newSessionFromBoard")}
                    className={btn}
                >
                    <FilePlus2 size={18} />
                </button>
            )}

            {onBoardStyle && (
                <>
                    <span className="mx-1 h-6 w-px bg-white/40" />
                    <button
                        type="button"
                        onClick={onBoardStyle}
                        title={t("constellation.boardStyle.title")}
                        aria-label={t("constellation.boardStyle.title")}
                        className={btn}
                    >
                        <Palette size={18} />
                    </button>
                </>
            )}

            <span className="mx-1 h-6 w-px bg-white/40" />
            <button
                type="button"
                onClick={onToggleNativeFullscreen}
                aria-pressed={nativeFullscreen}
                title={t("constellation.workspace.systemFullscreen")}
                aria-label={t("constellation.workspace.systemFullscreen")}
                className={`${btn} ${nativeFullscreen ? btnActive : ""}`}
            >
                {nativeFullscreen ? <Shrink size={18} /> : <Maximize2 size={18} />}
            </button>
            {onExit && (
                <button
                    type="button"
                    onClick={onExit}
                    title={t("constellation.workspace.exitImmersive")}
                    aria-label={t("constellation.workspace.exitImmersive")}
                    className={btn}
                >
                    <Minimize2 size={18} />
                </button>
            )}
        </div>
    );
}
